import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

import { Icon } from "@/components/Icon";
import { Toggle } from "@/components/Toggle";
import { useClients } from "@/hooks/useClients";
import { useAllAccess, useGrantAccess, useRevokeAccess } from "@/hooks/useAccess";
import { useMountpoints } from "@/hooks/useMountpoints";
import { extractApiError } from "@/lib/api";
import type { Client, Mountpoint } from "@/types/api";

/**
 * ACL editor: elige un cliente a la izquierda, gestiona sus mountpoints
 * a la derecha. Evita la matriz cliente×mountpoint (inmanejable cuando
 * ambas listas crecen) a cambio de un editor maestro-detalle con
 * búsqueda y acciones masivas por cliente.
 */
export function AccessPage() {
  const mountpoints = useMountpoints();
  const clients = useClients();
  const allAccess = useAllAccess();

  const loading = mountpoints.isLoading || clients.isLoading || allAccess.isLoading;

  const accessIndex = useMemo(() => {
    const byClient = new Map<string, Map<string, string>>();
    allAccess.data?.forEach((row) => {
      if (!byClient.has(row.client_id)) byClient.set(row.client_id, new Map());
      byClient.get(row.client_id)!.set(row.mountpoint_id, row.id);
    });
    return byClient;
  }, [allAccess.data]);

  const [clientQuery, setClientQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const filteredClients = useMemo(() => {
    const list = clients.data ?? [];
    if (!clientQuery) return list;
    const q = clientQuery.toLowerCase();
    return list.filter(
      (c) => c.name.toLowerCase().includes(q) || c.username.toLowerCase().includes(q),
    );
  }, [clients.data, clientQuery]);

  useEffect(() => {
    if (selectedId || !clients.data || clients.data.length === 0) return;
    setSelectedId(clients.data[0].id);
  }, [clients.data, selectedId]);

  const selectedClient = clients.data?.find((c) => c.id === selectedId) ?? null;

  return (
    <div className="view">
      <div className="view__head">
        <div className="view__title-block">
          <div className="view__eyebrow">
            <span className="tick" /> Permisos · Listas de control
          </div>
          <h1 className="view__title">Accesos (ACL)</h1>
          <p className="view__subtitle">
            Elige un cliente y activa los mountpoints que puede consumir. Los cambios se aplican
            al instante, uno por uno.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="card card--ticks" style={{ padding: 20 }}>
          <span className="sk" style={{ height: 320 }} />
        </div>
      ) : !mountpoints.data || !clients.data ? null : mountpoints.data.length === 0 ||
        clients.data.length === 0 ? (
        <div className="card card--ticks">
          <div className="empty">
            <div className="empty__reticle">
              <Icon.Key style={{ width: 18, height: 18 }} />
            </div>
            <div className="empty__title">Faltan datos para configurar accesos</div>
            <div className="empty__sub">
              Crea al menos un mountpoint y un cliente antes de gestionar accesos.
            </div>
          </div>
        </div>
      ) : (
        <div className="card card--ticks acl-layout">
          <div className="acl-list">
            <div className="acl-list__search search-pill">
              <Icon.Search style={{ width: 14, height: 14, opacity: 0.6 }} />
              <input
                value={clientQuery}
                onChange={(e) => setClientQuery(e.target.value)}
                placeholder="Buscar cliente…"
              />
            </div>
            <div className="acl-list__items">
              {filteredClients.length === 0 ? (
                <div className="acl-list__empty muted">Sin resultados</div>
              ) : (
                filteredClients.map((c) => {
                  const granted = accessIndex.get(c.id)?.size ?? 0;
                  return (
                    <button
                      key={c.id}
                      type="button"
                      className={`acl-list__item${c.id === selectedId ? " is-active" : ""}`}
                      onClick={() => setSelectedId(c.id)}
                    >
                      <span className="acl-list__item-main">
                        <span className="acl-list__item-name">{c.name}</span>
                        <span className="acl-list__item-user mono muted">{c.username}</span>
                      </span>
                      <span
                        className={`badge ${granted > 0 ? "badge--ok" : "badge--neutral"}`}
                        title={`${granted} de ${mountpoints.data!.length} mountpoints autorizados`}
                      >
                        {granted}/{mountpoints.data!.length}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
          </div>

          {selectedClient ? (
            <AccessPanel
              key={selectedClient.id}
              client={selectedClient}
              mountpoints={mountpoints.data}
              grantedIds={accessIndex.get(selectedClient.id) ?? new Map()}
            />
          ) : (
            <div className="acl-panel acl-panel--empty">
              <div className="empty">
                <div className="empty__reticle">
                  <Icon.Key style={{ width: 18, height: 18 }} />
                </div>
                <div className="empty__title">Selecciona un cliente</div>
                <div className="empty__sub">
                  Elige un cliente de la lista para ver y editar sus accesos.
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function AccessPanel({
  client,
  mountpoints,
  grantedIds,
}: {
  client: Client;
  mountpoints: Mountpoint[];
  grantedIds: Map<string, string>;
}) {
  const grant = useGrantAccess();
  const revoke = useRevokeAccess();
  const [query, setQuery] = useState("");
  const [bulkPending, setBulkPending] = useState(false);

  const filtered = useMemo(() => {
    if (!query) return mountpoints;
    const q = query.toLowerCase();
    return mountpoints.filter(
      (m) => m.name.toLowerCase().includes(q) || (m.description ?? "").toLowerCase().includes(q),
    );
  }, [mountpoints, query]);

  const grantedCount = grantedIds.size;

  const toggleOne = async (mountpoint: Mountpoint, next: boolean) => {
    if (next) {
      try {
        await grant.mutateAsync({ client_id: client.id, mountpoint_id: mountpoint.id });
        toast.success(`${client.name} → ${mountpoint.name} concedido`);
      } catch (err) {
        toast.error(extractApiError(err));
      }
    } else {
      const accessId = grantedIds.get(mountpoint.id);
      if (!accessId) return;
      try {
        await revoke.mutateAsync(accessId);
        toast.success(`${client.name} → ${mountpoint.name} revocado`);
      } catch (err) {
        toast.error(extractApiError(err));
      }
    }
  };

  const bulkSet = async (next: boolean) => {
    const targets = filtered.filter((m) => grantedIds.has(m.id) !== next);
    if (targets.length === 0) return;
    setBulkPending(true);
    const results = await Promise.allSettled(
      targets.map((m) =>
        next
          ? grant.mutateAsync({ client_id: client.id, mountpoint_id: m.id })
          : revoke.mutateAsync(grantedIds.get(m.id)!),
      ),
    );
    setBulkPending(false);
    const failed = results.filter((r) => r.status === "rejected").length;
    const ok = results.length - failed;
    if (ok > 0) {
      toast.success(
        next
          ? `${ok} mountpoint${ok === 1 ? "" : "s"} concedido${ok === 1 ? "" : "s"} a ${client.name}`
          : `${ok} mountpoint${ok === 1 ? "" : "s"} revocado${ok === 1 ? "" : "s"} a ${client.name}`,
      );
    }
    if (failed > 0) {
      toast.error(`${failed} cambio${failed === 1 ? "" : "s"} fallaron`);
    }
  };

  const busy = grant.isPending || revoke.isPending || bulkPending;
  const allFilteredGranted = filtered.length > 0 && filtered.every((m) => grantedIds.has(m.id));

  return (
    <div className="acl-panel">
      <div className="acl-panel__head">
        <div>
          <div className="acl-panel__title">{client.name}</div>
          <div className="mono muted" style={{ fontSize: 12 }}>
            {client.username} · {grantedCount}/{mountpoints.length} mountpoints autorizados
          </div>
        </div>
        <div className="row" style={{ gap: 8 }}>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy || allFilteredGranted}
            onClick={() => bulkSet(true)}
          >
            Seleccionar {query ? "filtrados" : "todos"}
          </button>
          <button
            type="button"
            className="btn btn--ghost"
            disabled={busy || filtered.every((m) => !grantedIds.has(m.id))}
            onClick={() => bulkSet(false)}
          >
            Quitar {query ? "filtrados" : "todos"}
          </button>
        </div>
      </div>

      <div className="acl-panel__search search-pill">
        <Icon.Search style={{ width: 14, height: 14, opacity: 0.6 }} />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar mountpoint…"
        />
      </div>

      <div className="acl-panel__rows">
        {filtered.length === 0 ? (
          <div className="acl-list__empty muted">Sin resultados</div>
        ) : (
          filtered.map((mp) => {
            const checked = grantedIds.has(mp.id);
            return (
              <div className="acl-row" key={mp.id}>
                <Toggle
                  checked={checked}
                  disabled={busy}
                  ariaLabel={`Acceso ${client.name} a ${mp.name}`}
                  onChange={(next) => toggleOne(mp, next)}
                />
                <div className="acl-row__meta">
                  <span className="acl-row__name">{mp.name}</span>
                  {mp.description && (
                    <span className="acl-row__desc muted">{mp.description}</span>
                  )}
                </div>
                {!mp.is_enabled && <span className="badge badge--off">Inactivo</span>}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
